import * as Errors from "../../shared/node/Errors";

import {
  CreateBookingsWithStripeInvoiceDocument,
  CreateBookingsWithStripeInvoiceMutation,
  CreateBookingsWithStripeInvoiceMutationVariables,
} from "../GeneratedGraphQL";
import { defaultOrganisation, testOrganisation2 } from "./organisation";
import { defaultService, tiers } from "./service";
import {
  userDetailsStripeInvoice,
  userDetailsStripeInvoiceWithoutCrmData,
} from "./userDetails";

import { BookingData } from "../AppDataProvider/AppDataProvider";
import { defaultBookingTool } from "./bookingTool";
import { defaultRecurrence } from "./bookings";
import { format } from "date-fns";
import { getBookingQuestionInput } from "../utils";
import { GraphQLError } from "graphql";
import { MockedResponse } from "@apollo/client/testing";
import { successReservedSpaceId } from "./createBookingsForBookingTool";
// For completely inscrutable reasons, this doesn't work if you import from
// utils directly ¯\_(ツ)_/¯
import { today } from "../utils/today";

const timezone = "Europe/London";
export const successReservedSpaceWithInvoiceId = "888777666555444333222123";
const errorReservedSpaceId = "888777666555444333222777";
const organisation = defaultOrganisation;
export const successConfirmBookingWithInvoiceId = "888777666555444333222332";

export const mockConfirmedBookingInvoiceSuccessData: BookingData = {
  organisation,
  reservedSpaceId: successReservedSpaceWithInvoiceId,
  startDate: new Date("2022-11-16T09:00:00.000Z"),
  timezone,
  userDetails: userDetailsStripeInvoice,
};

export const mockConfirmedBookingInvoiceErrorData: BookingData = {
  organisation,
  reservedSpaceId: errorReservedSpaceId,
  startDate: new Date("2022-11-16T09:00:00.000Z"),
  timezone,
  userDetails: userDetailsStripeInvoice,
};

const requestVariables: CreateBookingsWithStripeInvoiceMutationVariables = {
  bookings: [
    {
      bookingNotes: "Stripe payment notes",
      bookingQuestions: getBookingQuestionInput({
        bookingQuestionData: {},
        bookingQuestions: organisation.externalBookingQuestions,
        pickedServiceId: defaultService._id,
      }),
      customer: {
        ...userDetailsStripeInvoice,
        crmFieldData: [
          { id: "date_field", value: format(today, "yyyy-MM-dd") },
          { id: "optional_field", value: "" },
          { id: "text_field", value: "this is a text field" },
        ],
      },
      organisationId: organisation._id,
      reservedSpaceId: successReservedSpaceWithInvoiceId,
      timezone: timezone,
    },
  ],
  bookingToolId: defaultBookingTool._id,
  cardToken: "mockPaymentIntentId",
  createdSource: "test-app",
  locale: "en-GB",
};

export const createBookingsWithStripeInvoiceRequestSuccess: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: requestVariables,
    },
    result: {
      data: {
        createBookingsWithStripeInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithInvoiceId }],
        },
      },
    },
  };

const multiTierRequestVariables: CreateBookingsWithStripeInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: testOrganisation2.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsStripeInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: testOrganisation2._id,
        pricePoints: [
          { pricePointId: tiers[0].pricePoints[0]._id, quantity: 2 },
        ],
        reservedSpaceId: successReservedSpaceId,
        tierId: tiers[0]._id,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "mockPaymentIntentId",
    createdSource: "test-app",
    locale: "en-GB",
  };

export const createMultiTierBookingsWithStripeInvoiceRequestSuccess: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: multiTierRequestVariables,
    },
    result: {
      data: {
        createBookingsWithStripeInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithInvoiceId }],
        },
      },
    },
  };

const discountRequestVariables: CreateBookingsWithStripeInvoiceMutationVariables =
  {
    ...requestVariables,
    discount: {
      customerEmail: userDetailsStripeInvoice.email,
      discountCode: "TEST_CODE",
    },
  };

export const createBookingsWithDiscountedStripeInvoiceRequestSuccess: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: discountRequestVariables,
    },
    result: {
      data: {
        createBookingsWithStripeInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithInvoiceId }],
        },
      },
    },
  };
const requestVariablesWithoutCrmData: CreateBookingsWithStripeInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: organisation.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsStripeInvoiceWithoutCrmData,
          crmFieldData: [],
        },
        organisationId: organisation._id,
        reservedSpaceId: successReservedSpaceWithInvoiceId,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "mockPaymentIntentId",
    createdSource: "test-app",
    locale: "en-GB",
  };

export const createBookingsWithStripeInvoiceWithoutCrmDataRequestSuccess: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: requestVariablesWithoutCrmData,
    },
    result: {
      data: {
        createBookingsWithStripeInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithInvoiceId }],
        },
      },
    },
  };

const recurringBookingPaymentVariables: CreateBookingsWithStripeInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingIds: defaultRecurrence.bookings.map((booking) => booking._id),
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: testOrganisation2.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsStripeInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: testOrganisation2._id,
        reservedSpaceId: successReservedSpaceWithInvoiceId,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "mockPaymentIntentId",
    createdSource: "test-app",
    locale: "en-GB",
  };

export const createRecurringBookingsWithStripeInvoiceRequestSuccess: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: recurringBookingPaymentVariables,
    },
    result: {
      data: {
        createBookingsWithStripeInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithInvoiceId }],
        },
      },
    },
  };

export const createBookingsWithStripeInvoiceRequestError: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: requestVariables,
    },
    result: { errors: [new GraphQLError("Failed to create bookings")] },
  };

export const invalidOrAlteredCreateBookingsWithStripeInvoiceRequestError: MockedResponse<CreateBookingsWithStripeInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithStripeInvoiceDocument,
      variables: requestVariables,
    },
    result: { errors: [new Errors.UserInputError("Something's changed")] },
  };
